/**
 * The amortisation as something you can read: the payments grouped into years.
 *
 * Nothing here recomputes anything — every figure is a payment `nextPayment`
 * already made, summed or renamed. That is the point of the drill-down: what it
 * shows has to be the arithmetic the totals were made from, not a second
 * calculation that agrees with them today.
 */

import type { Mortgage, Payment } from './mortgage.ts';
import { monthlyCost } from './mortgage.ts';

export interface ScheduleMonth {
    /** 1-based, for reading. `Payment.month_count` counts from 0. */
    month: number;
    paid: number;
    interest: number;
    /** The part of the payment that is not interest. */
    off_loan: number;
    /** What is left owing once this payment has been made. */
    balance: number;
    rate: number;
    /**
     * A month that pays off what is left rather than the usual charge, and is
     * meaningfully smaller for it — the last month of a mortgage paid at a flat
     * rate a month, where the debt does not divide into whole payments.
     *
     * Meaningfully, because a mortgage from `findMortgage` also ends a little
     * short for reasons that are only rounding — see PART_PAYMENT.
     */
    part_payment: boolean;
}

export interface ScheduleYear {
    /** 1-based, like the months. */
    year: number;
    months: ScheduleMonth[];
    paid: number;
    interest: number;
    off_loan: number;
    /** The balance after the year's last payment. */
    balance: number;
    /**
     * Every rate charged during the year, in the order they were charged. Two
     * of them for the year the fixed term ends in — naming one would be a
     * quiet lie about half the year.
     */
    rates: number[];
}

/**
 * Payments that clear the debt, grouped into years of twelve.
 *
 * Zero-charge payments are dropped, so a mortgage found by `findMortgage` (which
 * pads its array out to the term it was asked for) and one from
 * `forMonthlyCost` (which stops when the debt is gone) both come out as the same
 * schedule. The last year is short unless the term lands on a whole year.
 */
/**
 * How far below the usual charge a payment has to fall to be worth marking:
 * more than a hundredth of it.
 *
 * There is a threshold at all because every mortgage ends a little short, the
 * last payment being whatever is left rather than the full charge. From
 * `findMortgage` that is only the penny it rounds up by, arriving 22p ahead
 * after 10 years, and a row 22p light needs no explaining. From a flat payment
 * a month it is whatever the debt happens to come to — £854 against £1,500 —
 * which does.
 */
const PART_PAYMENT = 0.99;

export function scheduleYears(mortgage: Mortgage): ScheduleYear[] {
    const years: ScheduleYear[] = [];
    const usual = monthlyCost(mortgage);

    mortgage.payments
        .filter((payment) => payment.charge !== 0)
        .forEach((payment, index) => {
            if (index % 12 === 0) {
                years.push(emptyYear(years.length + 1));
            }
            add(years[years.length - 1], month(payment, index, usual));
        });

    return years;
}

function month(payment: Payment, index: number, usual: number): ScheduleMonth {
    return {
        month: index + 1,
        paid: payment.charge,
        interest: payment.interest_charged,
        off_loan: payment.charge - payment.interest_charged,
        balance: payment.remaining_debt,
        rate: payment.annual_interest_rate,
        part_payment: payment.charge < usual * PART_PAYMENT,
    };
}

function emptyYear(year: number): ScheduleYear {
    return {
        year,
        months: [],
        paid: 0,
        interest: 0,
        off_loan: 0,
        balance: 0,
        rates: [],
    };
}

function add(year: ScheduleYear, month: ScheduleMonth) {
    year.months.push(month);
    year.paid += month.paid;
    year.interest += month.interest;
    year.off_loan += month.off_loan;
    year.balance = month.balance;
    if (!year.rates.includes(month.rate)) {
        year.rates.push(month.rate);
    }
}
